import request from 'tools/request'
import { PAGINATION_LIMIT_VALUE_NUM, PAGINATION_NUM_VALUE_NUM } from 'constants/headers'
import { getPaginationHeaders } from 'tools/headers'
import {
  PaymentListParams,
  PaymentsListResponse,
  PaymentCreateRequest,
  PaymentResponse,
  AssignPaymentInvoiceResponse,
  AssignPaymentInvoiceRequest,
  PaymentUpdateRequest,
  PaymentUpdateCsvResponse,
  TotalPaymentListParams,
  TotalPaymentListResponse,
  PaymentInvoicesResponse,
  PaymentOverAdvancesResponse,
  UndoPaymentRequest,
  ExportPaymentsParamsRequest,
  CreatePaymentBatchRequest,
  PaymentLabelCollectionResponse,
  PaymentInvoiceImportedDataResponse,
  ClearPaymentBatchRequest,
  PaymentGlCodePreviewResponse,
} from 'types/payments'
import { AssociateInvoiceLabelResponse, LabelAssignRequest } from '../types/labels'
import { BaseResponse } from '../types/common'
import { DocumentListResponse } from 'types/document'

export const listAllPayments = async (
  params: PaymentListParams,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM
) => {
  const data = await request
    .get<PaymentsListResponse>('payments', {
      params: params,
      headers: getPaginationHeaders(pageNumber, pageLimit, true),
    })
    .then((res) => {
      return { data: res.data.payments, headers: res.headers }
    })
  return data
}

export const createPayment = async (payload: PaymentCreateRequest) => {
  const data = request.post<PaymentResponse>('/payments', payload).then((res) => {
    return res.data
  })
  return data
}

export const previewPaymentGlCode = async (account: string, paymentType: string) => {
  const data = await request
    .get<PaymentGlCodePreviewResponse>('payments/gl-code-preview', { params: { account, paymentType } })
    .then((res) => res.data)
  return data
}

export const createPaymentBatch = async (payload: CreatePaymentBatchRequest) => {
  const data = request.post<PaymentsListResponse>('/payments/payment-batch', payload).then((res) => {
    return res.data
  })
  return data
}

export const getPayment = async (id: string) => {
  const data = await request.get<PaymentResponse>(`payments/${id}`).then((res) => {
    return res.data
  })
  return data
}

export const assignPaymentToInvoice = (paymentId: string, payload: AssignPaymentInvoiceRequest | null) => {
  const data = request.patch<AssignPaymentInvoiceResponse>(`payments/${paymentId}/invoices`, payload).then((res) => {
    return res.data
  })
  return data
}

export const updatePayment = (paymentId: string, payload: PaymentUpdateRequest) => {
  const data = request.put<PaymentResponse>(`payments/${paymentId}`, payload).then((res) => {
    return res.data
  })
  return data
}

export const uploadCsv = async (inputFile: File[]) => {
  const formData = new FormData()
  formData.append('inputFile', inputFile[0])
  const data = await request.post<PaymentUpdateCsvResponse>('payments/csv-upload', formData).then((res) => {
    return res.data
  })
  return data
}

export const totalPaymentList = async (params: TotalPaymentListParams) => {
  const data = await request
    .get<TotalPaymentListResponse>('payments/total-payments', { params: params })
    .then((res) => {
      return { data: res.data.totalPaymentInfo }
    })
  return data
}

export const getPaymentInvoices = async (
  params: PaymentListParams,
  paymentId: string,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM
) => {
  const data = await request
    .get<PaymentInvoicesResponse>(`payments/${paymentId}/invoices`, {
      params: params,
      headers: getPaginationHeaders(pageNumber, pageLimit),
    })
    .then((res) => {
      return { data: res.data.invoices, headers: res.headers }
    })
  return data
}

export const getPaymentOverAdvances = async (
  paymentId: string,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM,
  sort?: string,
  q?: string
) => {
  const data = await request
    .get<PaymentOverAdvancesResponse>(`payments/${paymentId}/over-advances`, {
      params: { sort, q },
      headers: getPaginationHeaders(pageNumber, pageLimit),
    })
    .then((res) => {
      return { data: res.data.overAdvances, headers: res.headers }
    })
  return data
}

export const undoPayment = (payload: UndoPaymentRequest) => {
  const data = request.put<PaymentsListResponse>(`payments/undo-payments`, payload).then((res) => {
    return res.data
  })
  return data
}

export const exportPayments = async (params: ExportPaymentsParamsRequest) => {
  const data = await request.get<string>('payments/export', { params: params }).then((res) => {
    return res.data
  })
  return data
}

export const getPaymentLabels = async (paymentId: string) => {
  const data = await request.get<PaymentLabelCollectionResponse>('payments/' + paymentId + '/labels').then((res) => {
    return res.data
  })
  return data
}

export const associatePaymentLabel = async (paymentId: string, payload: LabelAssignRequest) => {
  const data = await request
    .patch<AssociateInvoiceLabelResponse>(`payments/${paymentId}/labels`, payload)
    .then((res) => {
      return res.data
    })
  return data
}

export const removeLabelFromPayment = async (paymentId: string, labelId: string) => {
  const data = await request.delete<BaseResponse>(`payments/${paymentId}/labels/${labelId}`).then((r) => r.data)
  return data
}

export const getImportedInvoiceData = async (paymentId: string) => {
  const data = await request
    .get<PaymentInvoiceImportedDataResponse>(`payments/${paymentId}/imported-invoices`)
    .then((res) => {
      return res.data
    })
  return data
}

export const deleteOutOfSystemInvoice = async (oosId: string) => {
  const data = await request.delete<BaseResponse>(`out-of-system-invoices/${oosId}`).then((res) => {
    return res.data
  })
  return data
}

export const clearPaymentBatch = async (payload: ClearPaymentBatchRequest) => {
  const data = await request.patch<PaymentsListResponse>('payments/payment-batch', payload).then((res) => {
    return res.data.payments
  })
  return data
}

export const getPaymentDocuments = async (
  paymentId: string,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM,
  sort: string
) => {
  const data = await request
    .get<DocumentListResponse>(`payments/${paymentId}/documents`, {
      params: { sort: sort },
      headers: getPaginationHeaders(pageNumber, pageLimit),
    })
    .then((res) => {
      return { data: res.data, headers: res.headers }
    })
  return data
}
